import type { Ref } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { readTenantRegistered } from '@/composables/useAccessibilityRemoteSetupGate'
import { isDeviceOwnerApp } from '@/helpers/androidAccessibility'
import { fetchAndroidBrightnessStatus } from '@/helpers/androidBrightness'
import { isBootSplashPresent } from '@/helpers/bootSplash'
import { isWriteSettingsSetupEligible } from '@/helpers/writeSettingsSetupEligibility'
import { getPlatform } from '@/platform'

export const WRITE_SETTINGS_SETUP_SKIPPED_KEY = 'LUCIMARK:WRITE_SETTINGS_SETUP_SKIPPED'

/** While the overlay is up, re-check the permission (operator grants it in system settings) and registration. */
const OVERLAY_REEVAL_MS = 1_500

function readSkipped(): boolean {
  try {
    return localStorage.getItem(WRITE_SETTINGS_SETUP_SKIPPED_KEY) === '1'
  }
  catch {
    return false
  }
}

function writeSkipped(): void {
  try {
    localStorage.setItem(WRITE_SETTINGS_SETUP_SKIPPED_KEY, '1')
  }
  catch {
    // ignore quota / private mode
  }
}

/**
 * First-run gate that asks for "Modify system settings" (WRITE_SETTINGS) so Studio can set the
 * system-wide brightness instead of the foreground-only window override. Same audience as the
 * Accessibility gate (unregistered Device Owner), hidden on screens that cannot steer their
 * backlight, once granted, once skipped, and while the Accessibility gate is on screen.
 */
export function useWriteSettingsSetupGate(opts: { blocked: Ref<boolean> }) {
  const eligible = ref(false)
  const skipped = ref(readSkipped())
  const splashGone = ref(!isBootSplashPresent())

  const showOverlay = computed(
    () => eligible.value && !skipped.value && splashGone.value && !opts.blocked.value
  )

  async function reevaluate(): Promise<void> {
    if (getPlatform() !== 'android') {
      eligible.value = false
      return
    }
    const [deviceOwner, brightness, tenantRegistered] = await Promise.all([
      isDeviceOwnerApp(),
      fetchAndroidBrightnessStatus(),
      readTenantRegistered()
    ])
    eligible.value = isWriteSettingsSetupEligible({ deviceOwner, tenantRegistered, brightness })
  }

  function skip(): void {
    writeSkipped()
    skipped.value = true
  }

  /** The operator granted the permission: hide now instead of waiting for the next re-check. */
  function markDone(): void {
    eligible.value = false
  }

  let overlayPoll: ReturnType<typeof setInterval> | null = null

  function stopOverlayPoll(): void {
    if (overlayPoll != null) {
      clearInterval(overlayPoll)
      overlayPoll = null
    }
  }

  onMounted(() => {
    void reevaluate()
    if (isBootSplashPresent()) {
      const tick = setInterval(() => {
        if (!isBootSplashPresent()) {
          splashGone.value = true
          clearInterval(tick)
        }
      }, 200)
      // Safety: never block forever if splash helpers misreport.
      setTimeout(() => {
        splashGone.value = true
        clearInterval(tick)
      }, 15_000)
    }
  })

  onBeforeUnmount(() => {
    stopOverlayPoll()
  })

  // Re-run while visible so a grant (or a mid-overlay Studio claim) dismisses the gate on its own.
  watch(showOverlay, (show) => {
    stopOverlayPoll()
    if (!show) {
      return
    }
    void reevaluate()
    overlayPoll = setInterval(() => {
      void reevaluate()
    }, OVERLAY_REEVAL_MS)
  })

  // The Accessibility gate finishing is what makes this one visible; re-check then.
  watch(opts.blocked, (blocked) => {
    if (!blocked) {
      void reevaluate()
    }
  })

  return { showOverlay, skip, markDone, reevaluate }
}
